import React, { useState } from 'react';
import { CalendarDays, ChevronDown } from 'lucide-react';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';
import { Calendar } from './ui/calendar';

/**
 * DateNavigator — shared date-jump control.
 *
 * Renders a labelled button that opens a single-select month-picker popover.
 * Use anywhere we want the operator to be able to jump to an arbitrary day
 * (Dispatch, Calendar) instead of stepping with prev/next arrows.
 *
 * Behavior contract:
 *   • Clicking the button toggles the popover.
 *   • Selecting a date calls onChange with a Date object AND closes the popover.
 *   • The "selected" highlight on the Calendar mirrors `value`.
 *   • Pure presentation — owns no scheduling logic. Callers decide what
 *     "the current date" means (Dispatch = dispatchDate; Calendar = week
 *     anchor).
 *
 * Props
 * -----
 * value    : Date           — current selection
 * onChange : (Date) => void — fires on pick (popover auto-closes)
 * label    : string         — visible button text (e.g. "Today — Mon, Jun 30")
 * testId   : string         — data-testid on the trigger button
 * variant  : 'subtle' | 'pill'
 *            subtle — soft hover (used in Dispatch header)
 *            pill   — bordered pill (used in Calendar header)
 * align    : 'start' | 'center' | 'end' — Popover align
 * className: string         — extra classes on the trigger
 */
const TRIGGER_VARIANTS = {
  subtle: 'flex items-center gap-1.5 text-sm text-gray-500 mt-0.5 px-2 py-1 -ml-2 rounded-md hover:bg-gray-100 hover:text-gray-800 transition-colors group cursor-pointer',
  pill:   'inline-flex items-center gap-1.5 h-8 px-3 text-sm font-medium text-gray-700 border border-gray-200 rounded-md bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors group cursor-pointer',
};

const DateNavigator = ({
  value,
  onChange,
  label,
  testId,
  variant = 'subtle',
  align = 'start',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const triggerClass = `${TRIGGER_VARIANTS[variant] || TRIGGER_VARIANTS.subtle} ${className}`.trim();

  const handleSelect = (d) => {
    if (!d) return;
    onChange(d);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={triggerClass}
          data-testid={testId}
          aria-label="Jump to date"
        >
          <CalendarDays className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 transition-colors shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown className="h-3 w-3 text-gray-400 group-hover:text-gray-600 transition-colors shrink-0 ml-0.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateNavigator;
